import {createAsyncThunk} from '@reduxjs/toolkit'
import {api2Endpoint} from 'app/config/apiConfig'
import {deleteData} from 'app/hooks/api/useDeleteData'
import {getData} from 'app/hooks/api/useGetData'
import {postData} from 'app/hooks/api/usePostData'
import {putData} from 'app/hooks/api/usePutData'
import {ISavedSearchFE} from 'interfaces/ISavedSearchFE'

//About thunks:
//Create Async Thunks for handling async api calls when redux should be handling the state for global state management.
//Use local stte in functions and custom hooks for individual components when state management is not needed globally.

export const fetchSavedSearches = createAsyncThunk('savedSearch/fetchSavedSearches', async () => {
  try {
    const response = await getData<ISavedSearchFE[]>(api2Endpoint(`std/saved_search/recent`))
    if (!response) {
      throw new Error('Failed to fetch recent saved search')
    }
    //return an empty array if response is not an array since this is most likely going to always be called
    //conditionally based on if it is undefined or not rather than on how many recent searches a user has
    //it will b the case that there are no recent searches so response should be empty array in that case but this
    //is just in case the backend fails to return an empty array
    if (!Array.isArray(response)) {
      return []
    }
    // return metadata, notify, and recent (all saved searches and sort them out in redux)
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
  }
})

export const fetchSavedSearch = createAsyncThunk(
  'savedSearch/fetchSavedSearch',
  async (args: {searchId: string}) => {
    const {searchId} = args
    try {
      const response = await getData<ISavedSearchFE>(api2Endpoint(`std/saved_search/${searchId}`))
      if (!response) {
        throw new Error('Failed to fetch saved search')
      }
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const updateSavedNotificationSearch = createAsyncThunk(
  'savedSearch/updateSavedNotificationSearch',
  async (args: {id: string; search: ISavedSearchFE; originalSavedSearch: ISavedSearchFE}) => {
    const {id, search, originalSavedSearch} = args
    // only submit fields that can be edited
    const data: Partial<ISavedSearchFE> = {
      name: search.name,
      keywords: search.keywords,
      concepts: search.concepts,
      algorithm: search.algorithm,
      is_permasaved: true,
      is_metadata: false,
      is_notify: true,
      is_paused: search.is_paused,
      notify_entities: search.notify_entities,
      search_text: search.search_text,
    }
    try {
      const response = await putData<ISavedSearchFE>(api2Endpoint(`std/saved_search/${id}`), data)
      if (!response) {
        throw new Error('Failed to update saved search')
      } else {
        //since api does not return response of updated object, just update the redux store if update is successful
        const id = response.id
        const newSavedSearch = {...originalSavedSearch, ...data}
        return newSavedSearch
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const updateRecentSavedSearch = createAsyncThunk(
  'savedSearch/updateSavedSearch',
  async (args: {
    id: string
    data: Partial<ISavedSearchFE>
    originalSavedSearch: ISavedSearchFE
  }) => {
    const {id, data, originalSavedSearch} = args
    try {
      const response = await putData<ISavedSearchFE>(
        api2Endpoint(`std/saved_search/recent/${id}`),
        data
      )
      if (!response) {
        throw new Error('Failed to update saved search')
      } else {
        //since api does not return response of updated object, just update the redux store if update is successful
        const id = response.id
        const newSavedSearch = {...originalSavedSearch, ...data}
        return newSavedSearch
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const deleteSavedSearch = createAsyncThunk(
  'savedSearch/deleteSavedSearches',
  async (args: {id: string}) => {
    try {
      const response = await deleteData<ISavedSearchFE[]>(
        api2Endpoint(`std/saved_search/${args.id}`)
      )
      if (!response) {
        throw new Error('Failed to delete saved search')
      }
      //returns id of recent search that was deleted as payload to the savedsearchslice redux which removes the search from the list
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

//change to classifytext operations
export const classifyTextWRecentSearch = createAsyncThunk(
  'savedSearch/classifyTextWRecentSearchUpdate',
  async (
    args: {
      search_text: string
      isUpdate: boolean
      originalSavedSearch: ISavedSearchFE
      per_page?: number
      pages?: number
    },
    {rejectWithValue}
  ) => {
    const {search_text, isUpdate, originalSavedSearch, per_page, pages} = args

    try {
      const postBody = {
        search: originalSavedSearch,
        page: pages ?? 1,
        per_page: per_page ?? 10,
        related_to_text: search_text,
      }
      const route = api2Endpoint(`graph/concept`)
      const conceptResponse = await postData<any>(route, postBody)
      if (conceptResponse?.records.length === 0) {
        //not sure how this works yet or if I'm suposed to supply the callback
        return rejectWithValue('Error fetching data from URL 1')
      } else {
        const concepts = conceptResponse.records?.map((c: any) => {
          return {
            ...c,
            __weight: 1,
            __lock: null,
          }
        })
        const newConcepts = concepts.map((c: any) => {
          delete c.__score
          return c
        })
        const postBodyRS = {
          search_text: search_text,
          concepts: newConcepts,
          algorithm: 'standard',
          keywords: originalSavedSearch.keywords,
          created: originalSavedSearch.created,
          updated: originalSavedSearch.updated,
          project_id: originalSavedSearch.project_id,
          name: originalSavedSearch.name,
        }
        let respSavedSearch: any
        if (isUpdate) {
          respSavedSearch = await putData<ISavedSearchFE>(
            api2Endpoint(`std/saved_search/recent/${args.originalSavedSearch.id}`),
            postBodyRS
          )
        } else {
          respSavedSearch = await postData<any>(api2Endpoint('std/saved_search/recent'), postBodyRS)
        }
        //Since the api does not return concepts with a name, we have to get the list of saved recents and filter the
        //search we just saved or we don't get concept names
        const retRightSearch = respSavedSearch.id
        //this gets all the recent searches, but we are singling out the one we just saved
        const respGetSavdSearches = await getData<any>(api2Endpoint('std/saved_search/recent'))
        if (respGetSavdSearches?.length === 0) {
          return rejectWithValue('Error fetching data from URL 1')
        } else {
          const correctSearchToRetrn = respGetSavdSearches?.find(
            (f: ISavedSearchFE) => f.id === retRightSearch
          )
          //Gotta filter ones that are recent searches
          const filtSavedSearches = ((respGetSavdSearches as ISavedSearchFE[]) ?? [])?.filter(
            (f) => f.project_id === null
          )
          return {
            recentSearches: filtSavedSearches,
            savedSearch: correctSearchToRetrn as ISavedSearchFE,
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const classifyTextWNotifySearch = createAsyncThunk(
  'savedSearch/classifyTextWNotifySearchUpdate',
  async (
    args: {
      search_text: string
      isUpdate: boolean
      originalSavedSearch: ISavedSearchFE
      per_page?: number
      pages?: number
    },
    {rejectWithValue}
  ) => {
    const {search_text, isUpdate, originalSavedSearch, per_page, pages} = args

    try {
      const postBody = {
        search: originalSavedSearch,
        page: pages ?? 1,
        per_page: per_page ?? 10,
        related_to_text: search_text,
      }
      const route = api2Endpoint(`graph/concept`)
      const conceptResponse = await postData<any>(route, postBody)
      if (conceptResponse?.records.length === 0) {
        //not sure how this works yet or if I'm suposed to supply the callback
        return rejectWithValue('Error fetching data from URL 1')
      } else {
        const concepts = conceptResponse.records?.map((c: any) => {
          return {
            ...c,
            __weight: 1,
            __lock: null,
          }
        })
        const newConcepts = concepts.map((c: any) => {
          delete c.__score
          return c
        })
        const postBodyNS = {
          search_text: search_text,
          concepts: newConcepts,
          algorithm: 'standard',
          keywords: originalSavedSearch.keywords,
          created: originalSavedSearch.created,
          updated: originalSavedSearch.updated,
          project_id: null,
          name: originalSavedSearch.name,
          is_permasaved: true,
          is_metadata: false,
          is_notify: true,
        }
        let respSavedSearch: any
        if (isUpdate) {
          respSavedSearch = await putData<ISavedSearchFE>(
            api2Endpoint(`std/saved_search/${args.originalSavedSearch.id}`),
            postBodyNS
          )
        } else {
          respSavedSearch = await postData<any>(api2Endpoint('std/saved_search'), postBodyNS)
        }
        //Since the api does not return concepts with a name, we have to get the list of saved recents and filter the
        //search we just saved or we don't get concept names
        const retRightSearch = respSavedSearch.id
        //this gets all the recent searches, but we are singling out the one we just saved
        const respGetSavdSearches = await getData<any>(api2Endpoint('std/saved_search'))
        if (respGetSavdSearches?.length === 0) {
          return rejectWithValue('Error fetching data from URL 1')
        } else {
          const correctSearchToRetrn = respGetSavdSearches?.find(
            (f: ISavedSearchFE) => f.id === retRightSearch
          )
          //Gotta filter ones that are recent searches
          const filtSavedSearches = ((respGetSavdSearches as ISavedSearchFE[]) ?? [])?.filter(
            (f) => f.is_notify === true && f.project_id === null
          )
          return {
            notifySearches: filtSavedSearches,
            savedSearch: correctSearchToRetrn as ISavedSearchFE,
          }
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const classifyTextOnly = createAsyncThunk(
  'savedSearch/classifyTextOnly',
  async (
    args: {
      search_text: string
      originalSavedSearch: ISavedSearchFE
      per_page?: number
      pages?: number
    },
    {rejectWithValue}
  ) => {
    const {search_text, originalSavedSearch, per_page, pages} = args

    try {
      const postBody = {
        search: originalSavedSearch,
        page: pages ?? 1,
        per_page: per_page ?? 10,
        related_to_text: search_text,
      }
      const route = api2Endpoint(`graph/concept`)
      const conceptResponse = await postData<any>(route, postBody)
      if (conceptResponse?.records.length === 0) {
        //not sure how this works yet or if I'm suposed to supply the callback
        return rejectWithValue('Error fetching data from URL 1')
      } else {
        const concepts = conceptResponse.records?.map((c: any) => {
          return {
            ...c,
            __weight: 1,
            __lock: null,
          }
        })
        const newConcepts = concepts.map((c: any) => {
          delete c.__score
          return c
        })
        const postBodyNS = {
          id: originalSavedSearch.id,
          search_text: search_text,
          concepts: newConcepts,
          algorithm: 'standard',
          keywords: originalSavedSearch.keywords,
          created: originalSavedSearch.created,
          updated: originalSavedSearch.updated,
          project_id: null,
          name: originalSavedSearch.name,
          is_permasaved: true,
          is_metadata: false,
          is_notify: true,
        }
        const retval = postBodyNS as ISavedSearchFE
        return retval
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)

export const saveNewNotifySearch = createAsyncThunk(
  'savedSearch/saveNewNotifySearch',
  async (args: {search: ISavedSearchFE}) => {
    const {search} = args
    try {
      const postBody = {
        search_text: search.search_text,
        concepts: search.concepts,
        algorithm: search.algorithm,
        keywords: search.keywords,
        notify_entities: search.notify_entities,
        name: search.name,
        is_permasaved: true,
        is_metadata: false,
        is_notify: true,
        is_paused: false,
      }
      const response = await postData<ISavedSearchFE>(api2Endpoint('std/saved_search'), postBody)
      if (!response) {
        throw new Error('Failed to save new notify search')
      }
      const newSavedSearch = {...response, ...postBody}
      return newSavedSearch
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
    }
  }
)
